import { Button } from "web-design-system";
import { useCallback, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import omit from "lodash/omit";
import { useFormContext } from "react-hook-form";
import { z } from "zod";
import { handleError } from "@/utils/error";
import { isDev } from "@/utils/common";
import { ArrowRight } from "lucide-react";
import {
  ProposalFormSchema,
  useProposalBuilderContext,
} from "../../providers/proposal-provider";
import { useSaveProposal } from "../../hooks/use-save-proposal";
import { useSaveProposalFunds } from "../../hooks/use-save-proposal-funds";
import { AllocationStatus } from "./allocation-status";

export function ProposalFooter() {
  const { uiState, next } = useProposalBuilderContext();

  const form = useFormContext<z.infer<typeof ProposalFormSchema>>();

  const proposal = form.getValues();

  const { goal } = proposal;

  const funds = form.watch("proposal_funds");

  const navigate = useNavigate();

  const saveProposal = useSaveProposal();

  const saveProposalFunds = useSaveProposalFunds();

  const handleClose = useCallback(
    () =>
      navigate({
        to: ".",
        search: (prev) =>
          omit(prev, ["client_id", "modal", "proposal_id", "state"]),
        replace: true,
      }),
    [navigate]
  );

  const [inProgress, setInProgress] = useState(false);

  const handleContinue = useCallback(async () => {
    try {
      setInProgress(true);
      switch (uiState) {
        case "init":
          next();
          break;
        case "setup":
          await saveProposal();
          next();
          break;
        case "lumpsum":
        case "sip":
          await saveProposalFunds();
          next();
          break;
        case "remarks":
          await Promise.all([saveProposal(true), saveProposalFunds()]);
          next();
          break;
        case "download": {
          handleClose();
          break;
        }
        default:
          break;
      }
    } catch (error) {
      handleError(error, "Failed to update proposal");
    } finally {
      setInProgress(false);
    }
  }, [uiState, saveProposal, saveProposalFunds, next, handleClose]);

  const canContinue = () => {
    switch (uiState) {
      case "init":
        if (goal) return true;
        return !!proposal.name;
      case "setup":
        return (
          proposal.name.trim() !== "" &&
          (Number(proposal.total_lumpsum_amount) > 0 ||
            Number(proposal.total_sip_amount) > 0)
        );
      case "sip": {
        const proposed_sips = funds
          .filter(
            (f) => f.type === "SIP" && !Number.isNaN(Number(f.sip_amount))
          )
          .reduce((sum, f) => sum + Number(f.sip_amount), 0);
        return (
          Math.round(
            (Number(proposed_sips) / Number(proposal.total_sip_amount)) * 100
          ) === 100
        );
      }
      case "lumpsum": {
        const proposed_lumpsums = funds
          .filter(
            (f) =>
              f.type === "LUMPSUM" && !Number.isNaN(Number(f.lumpsum_amount))
          )
          .reduce((sum, f) => sum + Number(f.lumpsum_amount), 0);
        return (
          Math.round(
            (Number(proposed_lumpsums) /
              Number(proposal.total_lumpsum_amount)) *
              100
          ) === 100
        );
      }
      case "remarks": {
        const proposed_lumpsums = funds
          .filter(
            (f) =>
              f.type === "LUMPSUM" && !Number.isNaN(Number(f.lumpsum_amount))
          )
          .reduce((sum, f) => sum + Number(f.lumpsum_amount), 0);
        const lumpsumRequired = Number(proposal.total_lumpsum_amount);
        if (
          lumpsumRequired > 0 &&
          Math.round((proposed_lumpsums / lumpsumRequired) * 100) !== 100
        )
          return false;

        const proposed_sips = funds
          .filter(
            (f) => f.type === "SIP" && !Number.isNaN(Number(f.sip_amount))
          )
          .reduce((sum, f) => sum + Number(f.sip_amount), 0);
        const sipRequired = Number(proposal.total_sip_amount);
        if (
          sipRequired > 0 &&
          Math.round((proposed_sips / sipRequired) * 100) !== 100
        )
          return false;

        return true;
      }
      case "download":
        return true;
      default:
        return false;
    }
  };

  const getButtonText = () => {
    switch (uiState) {
      case "init":
        return "Continue";
      case "setup":
        return "Continue";
      case "sip":
        return "Add remarks";
      case "lumpsum":
        return "Continue";
      case "remarks":
        return "Generate Report";
      default:
        return "Continue";
    }
  };

  const getLeftContent = () => {
    switch (uiState) {
      case "init":
      case "setup":
        return null;
      case "sip": {
        const current = funds
          .filter(
            (f) => f.type === "SIP" && !Number.isNaN(Number(f.sip_amount))
          )
          .reduce((sum, f) => sum + Number(f.sip_amount), 0);
        return (
          <AllocationStatus
            current={current}
            total={Number(proposal.total_sip_amount)}
          />
        );
      }
      case "lumpsum": {
        const current = funds
          .filter(
            (f) =>
              f.type === "LUMPSUM" && !Number.isNaN(Number(f.lumpsum_amount))
          )
          .reduce((sum, f) => sum + Number(f.lumpsum_amount), 0);
        return (
          <AllocationStatus
            current={current}
            total={Number(proposal.total_lumpsum_amount)}
          />
        );
      }
      case "remarks":
      case "download": {
        return null;
      }
      default:
        return (
          <Button onClick={handleClose} variant="outline">
            Close
          </Button>
        );
    }
  };

  return (
    <div className="flex flex-row shrink-0 h-20 items-center gap-3 px-5 border-t border-border bg-background">
      {getLeftContent()}
      <div className="flex gap-2 ml-auto">
        {isDev() && uiState === "remarks" && (
          <Button
            variant="ghost"
            onClick={() =>
              window.open(
                `${window.location.origin}/reports/clients/${proposal.client}/proposal/${proposal.id}`,
                "_blank"
              )
            }
          >
            Show preview
          </Button>
        )}
        <Button
          onClick={handleContinue}
          disabled={!canContinue() || inProgress}
        >
          {getButtonText()}
          {uiState !== "download" && <ArrowRight />}
        </Button>
      </div>
    </div>
  );
}
